import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Button } from '../ui/button';
import { Check, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';
import { getImageUrl } from '../../utils/imageUtils';

export const BuddyRequests = ({ incomingRequests, outgoingRequests, onAccept, onReject, onCancel }) => {
  const { api } = useAuth();
  const [cancellingId, setCancellingId] = useState(null);

  const handleCancel = async (requestId) => {
    if (!window.confirm('Cancel this buddy request? The other person will no longer see it as pending.')) return;
    setCancellingId(requestId);
    try {
      await api().delete(`/buddies/requests/${requestId}`);
      toast.success('Request cancelled');
      if (onCancel) onCancel(requestId);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not cancel request');
    } finally {
      setCancellingId(null);
    }
  };

  return (
  <motion.div key="requests" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="max-w-2xl mx-auto">
    <h3 className="font-heading text-lg text-white uppercase tracking-wider mb-4">
      Incoming Requests ({incomingRequests.length})
    </h3>
    {incomingRequests.length === 0 ? (
      <p className="text-muted-foreground text-center py-8 bg-card border border-border mb-8">No pending requests</p>
    ) : (
      <div className="space-y-3 mb-8">
        {incomingRequests.map(req => (
          <div key={req.id} className="bg-card border border-border p-4 flex items-center gap-4">
            <Link to={`/user/${req.user?.id}`}>
              <Avatar className="h-12 w-12">
                <AvatarImage src={getImageUrl(req.user?.profile_picture)} />
                <AvatarFallback className="bg-primary/20 text-primary">{req.user?.name?.charAt(0)}</AvatarFallback>
              </Avatar>
            </Link>
            <div className="flex-1">
              <Link to={`/user/${req.user?.id}`} className="font-medium text-white hover:text-primary">{req.user?.name}</Link>
              <p className="text-sm text-muted-foreground">{req.user?.streak?.current_streak || 0} day streak</p>
            </div>
            <div className="flex gap-2">
              <Button size="sm" onClick={() => onAccept(req.id)} className="bg-primary text-black"><Check className="h-4 w-4" /></Button>
              <Button size="sm" variant="outline" onClick={() => onReject(req.id)}><X className="h-4 w-4" /></Button>
            </div>
          </div>
        ))}
      </div>
    )}

    <h3 className="font-heading text-lg text-white uppercase tracking-wider mb-4">
      Sent Requests ({outgoingRequests.length})
    </h3>
    {outgoingRequests.length === 0 ? (
      <p className="text-muted-foreground text-center py-8 bg-card border border-border">No pending sent requests</p>
    ) : (
      <div className="space-y-3">
        {outgoingRequests.map(req => (
          <div key={req.id} className="bg-card border border-border p-4 flex items-center gap-4">
            <Avatar className="h-12 w-12">
              <AvatarImage src={getImageUrl(req.user?.profile_picture)} />
              <AvatarFallback className="bg-primary/20 text-primary">{req.user?.name?.charAt(0)}</AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <p className="font-medium text-white">{req.user?.name}</p>
              <p className="text-sm text-yellow-400">Pending...</p>
            </div>
            {/* Cancel lets the sender withdraw a pending request so it stops
                sitting in the recipient's inbox forever. */}
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleCancel(req.id)}
              disabled={cancellingId === req.id}
              className="border-red-500/40 text-red-400 hover:bg-red-500/10 hover:text-red-300"
              data-testid={`cancel-buddy-request-${req.id}`}
            >
              {cancellingId === req.id ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <X className="h-4 w-4 mr-1" />
                  Cancel
                </>
              )}
            </Button>
          </div>
        ))}
      </div>
    )}
  </motion.div>
  );
};
